import * as React from "react";
import { useEffect, useState, useId } from "react";
import { Typography, Button, Tooltip, Spinner, Chip } from "@material-tailwind/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { observer } from "mobx-react-lite";
import "swiper/css/bundle";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { ArrowLeftCircleIcon } from "@heroicons/react/24/solid";
import { ArrowRightCircleIcon } from "@heroicons/react/20/solid";
import { Link, useNavigate } from "react-router-dom";

import { textualIssueOutletController } from "../../Controllers/TextualIssueOutletController";
import { issueDetailsController } from "../../Controllers/IssueDetailsController";
import { storageController } from "../../../../Common/Controllers/StoreControllerFactory";
import { OrderIssueController } from "../../../../Common/Controllers/Order/OrderIssueController";
import { StockistIssue } from "../../../../Common/DTOs/StockistIssue";
import { loginSignupController } from "../../../../Common/Components/Standalone/LoginOrSignup/LoginSignupController";
import { alertController } from "../../../../Common/Controllers/AlertController";
import { userAccountController } from "../../../../Common/Controllers/UserAccountController";
import { MAX_ISSUES_AVAILABLE } from "../../../../Common/DTOs/Stock";

export const UpcomingImageStack: React.FC<{ issue: StockistIssue; linkEnabled: boolean; daysUntil: number, labelText?: string, labelTextColorClass?: string }> = ({
  issue,
  linkEnabled,
  daysUntil,
  labelText,
  labelTextColorClass
}) => {
  const navigate = useNavigate();

  return (
    <div
      className={`w-[230px] h-[320px] flex-shrink-0 mx-auto relative mb-4 ${linkEnabled ? "cursor-pointer" : ""}`}
      onClick={() => { if (linkEnabled) { issueDetailsController.setMagazineHint(issue.magazineId); navigate(`/issue/${issue.id}`); } }}
    >
      {issue.imageAvailable ? (
        <img
          src={
            issue.imageURL_230x320 ||
            "/images/coming_soon_230x320.jpg"
          }
          alt={`${issue.identity} cover`}
          title={`See this issue of ${issue.magazine.titleDisplay}`}
          className={`w-[230px] h-[320px] transition duration-150 ease-in-out hover:shadow-[0_0_12px_#fff] object-cover ${issue.issueOnSale <= new Date() && issue.stock.stockAvailableForStockist <= 0
            ? "grayscale opacity-50"
            : ""
            }`}
        />
      ) : (
        <div className="relative w-full h-full flex flex-col items-center justify-center">
          {issue.previousIssuesImageURLs.length > 0 ?
            <>
              {(issue.previousIssuesImageURLs.slice(0, 1)).map((url, i) => {
                const positions = [
                  "z-10 rotate-0",
                  "z-20 rotate-2",
                  "z-30 rotate-3",
                ];

                return (
                  <img
                    key={i}
                    src={url}
                    alt={`Previous Issue ${i + 1}`}
                    title={`See this issue of ${issue.magazine.titleDisplay}`}
                    className={`transition duration-150 ease-in-out hover:shadow-[0_0_12px_#fff] hover:shadow-white brightness-[80%] grayscale-[20%] w-[230px] h-[320px] absolute object-cover shadow-lg top-0 left-0 ${positions[i] ?? "top-0 left-0"}`}
                  />
                );
              })
              }
              <span className={`absolute text-white/80 font-bold select-none pointer-events-none z-30 -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 rotate-[-45deg] uppercase text-2xl w-full text-center`}>Sample Cover</span>
            </> :
            <div
              title="No cover available"
              className="hover:shadow-[0_0_12px_#fff] hover:shadow-white flex flex-col items-center justify-center w-[230px] h-[320px] bg-main outline outline-1 outline-light-grey/50 object-cover shadow-sm transition-all duration-300"
            >
              <span className="text-white italic text-3xl font-bold text-center">NEW LAUNCH!</span>
            </div>
          }
          {issue.previousIssuesImageURLs.length > 0 ? (
            <span className="absolute -bottom-3 right-0 text-[10px] leading-none text-light-grey/50 italic">Past issue cover</span>
          ) : (
            <span className="absolute -bottom-3 right-0 text-[10px] leading-none text-light-grey/50 italic">
              No cover available
            </span>
          )}
        </div>
      )}
      {labelText && <Chip color="amber" value={labelText} className={`absolute bottom-0 left-0 z-10 bg-white text-sm px-1 md:px-2 py-1 rounded-none font-semibold border-t border-r border-main ${labelTextColorClass}`} />}
      <div
        className={`${daysUntil <= 1 ? "bg-red-500 animate-slowPulseElementRotated" : ""
          } absolute z-[35] bottom-2 right-0 bg-purple text-white text-sm font-semibold px-1 md:px-2 py-1 rounded shadow-[0_0_3px_1px_#aaa] border border-white transition duration-150 rotate-[-15deg] hover:scale-110 hover:shadow-[0_0_5px_2px_#aaa]`}
      >
        {daysUntil} day{daysUntil !== 1 ? "s" : ""}
      </div>
    </div>
  );
};

export const TextualIssueOutlet = observer(() => {
  const [weekStores, setWeekStores] = useState<OrderIssueController[]>([]);
  const [monthStores, setMonthStores] = useState<OrderIssueController[]>([]);
  const weekId = useId().replace(/:/g, "_");
  const monthId = useId().replace(/:/g, "_");

  useEffect(() => {
    setWeekStores(
      textualIssueOutletController.upcomingWeekIssues.map((issue) =>
        storageController.getOrCreateStore(issue)
      )
    );
  }, [textualIssueOutletController.upcomingWeekIssues.length]);

  useEffect(() => {
    setMonthStores(
      textualIssueOutletController.upcomingMonthIssues.map((issue) =>
        storageController.getOrCreateStore(issue)
      )
    );
  }, [textualIssueOutletController.upcomingMonthIssues.length]);

  const renderSection = (
    title: string,
    subtitle: string,
    issues: StockistIssue[],
    stores: OrderIssueController[],
    navId: string
  ) => {
    if (!issues || issues.length === 0) return null;
    const prevClass = `swiper-prev-${navId}`;
    const nextClass = `swiper-next-${navId}`;

    return (
      <section className="lgh:first:mt-4 pt-3 md:px-1 py-2" key={title}>
        <div className="flex flex-col justify-center gap-1 mb-4 md:px-2">
          <div className="flex flex-col sm:flex-row flex-wrap sm:items-end">
            <Typography
              variant="h2"
              className="text-white animate-fadeUp !text-2xl md:!text-3xl text-center sm:text-left mr-2"
            >
              {title}
            </Typography>
            <Typography
              variant="small"
              className="text-gray-500 italic animate-fadeUp text-center sm:text-left leading-6"
            >
              {subtitle}
            </Typography>
          </div>
          <div className="inline-block w-full md:w-[40%] my-2">
            <div className="h-[2px] bg-white animate-expandWidth"></div>
          </div>
        </div>

        <div className="w-full">
          <Swiper
            modules={[Navigation]}
            navigation={{ prevEl: `.${prevClass}`, nextEl: `.${nextClass}` }}
            spaceBetween={4}
            slidesPerView={1}
            centeredSlides={true}
            breakpoints={{
              315: { slidesPerView: 1.25, centeredSlides: false },
              390: { slidesPerView: 1.5, centeredSlides: false },
              474: { slidesPerView: 2, centeredSlides: false },
              620: { slidesPerView: 2.5, centeredSlides: false },
              718: { slidesPerView: 3, centeredSlides: false },
              860: { slidesPerView: 3.5, centeredSlides: false },
              962: { slidesPerView: 4, centeredSlides: false },
              1192: { slidesPerView: 5, centeredSlides: false },
              1426: { slidesPerView: 6, centeredSlides: false },
              1660: { slidesPerView: 7, centeredSlides: false },
              1894: { slidesPerView: 8, centeredSlides: false },
              2128: { slidesPerView: 9, centeredSlides: false },
              2362: { slidesPerView: 10, centeredSlides: false },
              2596: { slidesPerView: 11, centeredSlides: false },
              2830: { slidesPerView: 12, centeredSlides: false },
              3064: { slidesPerView: 13, centeredSlides: false },
            }}
            maxBackfaceHiddenSlides={2}
            className="!pt-3"
          >
            {issues.map((issue, idx) => (
              <SwiperSlide key={issue.id}>
                <UpcomingIssueCard issue={issue} localStore={stores[idx]} />
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="flex justify-center gap-4 mt-4">
            <Button className={`p-0 rounded-full ${prevClass}`}>
              <ArrowLeftCircleIcon className="w-14 h-14 hover:bg-gray-300 transition" />
            </Button>
            <Button className={`p-0 rounded-full ${nextClass}`}>
              <ArrowRightCircleIcon className="w-14 h-14 hover:bg-gray-300 transition" />
            </Button>
          </div>
        </div>
      </section>
    );
  };

  if (textualIssueOutletController.isLoadingUpcomingIssues)
    return <div className="flex justify-center mt-10 text-gray-400 animate-fadeUp"><Spinner className="w-12 h-12" /></div>

  return (
    <div className="min-h-[calc(100vh-66px)] md:min-h-[calc(100vh-130px)]">
      {renderSection(
        "Coming this week",
        "The following issues are imminent, add to basket to secure and receive notification!",
        textualIssueOutletController.upcomingWeekIssues,
        weekStores,
        weekId
      )}

      {renderSection(
        "Coming soon",
        "Issues due over the next couple of months",
        textualIssueOutletController.upcomingMonthIssues,
        monthStores,
        monthId
      )}

      {!textualIssueOutletController.isLoadingUpcomingIssues &&
        textualIssueOutletController.issues.length === 0 && (
          <div className="text-center mt-20 text-gray-400 animate-fadeUp">
            <Typography variant="h6">No upcoming issues found</Typography>
          </div>
        )}
    </div>
  );
});

export const UpcomingIssueCard: React.FC<{
  issue: StockistIssue;
  localStore?: OrderIssueController;
  isHighlighted?: boolean;
  labelText?: string;
  labelTextColorClass?: string;
}> = observer(({ issue, localStore, isHighlighted, labelText, labelTextColorClass }) => {
  const daysUntil = Math.ceil(
    (new Date(issue.issueOnSale).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
  const store = localStore || storageController.getOrCreateStore(issue);
  const canInc = store.orderedAmount < MAX_ISSUES_AVAILABLE;
  const canDec = store.orderedAmount > 0;

  const onInc = () =>
    store.onCopiesChange(Math.min(MAX_ISSUES_AVAILABLE, store.orderedAmount + 1));
  const onDec = () =>
    store.onCopiesChange(Math.max(0, store.orderedAmount - 1));

  const onAddToBasketButtonClick = () => {
    if (!userAccountController.isLoggedIn) {
      alertController.displayAlert(
        "Login required",
        "You have to be logged in to add magazines to your basket.",
        "warning",
        4000
      );
      loginSignupController.setLoginSignupWindowVisible(true);
      return;
    }
    store.confirmOrder(issue);
  };

  return (
    <div className="flex justify-center">
      <div className={`flex flex-col items-center justify-between animate-fadeUp w-full [490px]:w-[230px] h-[489px] ${isHighlighted ? "outline outline-3 outline-purple outline-offset-2 shadow shadow-purple" : ""}`}>
        <div className="h-full w-full flex flex-col items-center sm:items-start gap-0.5">
          <UpcomingImageStack issue={issue} linkEnabled daysUntil={daysUntil} labelText={labelText} labelTextColorClass={labelTextColorClass} />
          <div className="flex flex-col w-full items-start justify-between flex-auto">
            <div className="flex flex-col items-center sm:items-start w-full">
              <div className="text-xl font-bold w-full truncate text-center sm:text-left">
                <Link
                  className="transition duration-150 ease-in text-white hover:text-gray-400 cursor-pointer"
                  title={`See latest issues of ${issue.magazine.titleDisplay}`}
                  to={issue.magazine.stockistMagazineUrl}
                  onClick={() => issueDetailsController.setMagazineHint(issue.magazineId)}
                >
                  {issue.magazine.titleDisplay}
                </Link>
              </div>
              <div className="text-gray-500 flex justify-center sm:justify-start gap-1 w-full">
                <span title={issue.identity} className="truncate">{issue.identity}</span>
                <span>|</span>
                <span
                  className={`${daysUntil <= 1 ? "text-red-500 font-bold" : ""}`}
                  title={issue.onSaleDateDisplay}>{issue.onSaleDateDisplay}</span>
                <Tooltip content="Estimated on-sale date">
                  <InformationCircleIcon className="h-4 w-4 text-gray-300 cursor-pointer" />
                </Tooltip>
              </div>
            </div>
            <div className="flex gap-2 justify-center sm:justify-start items-center w-full">
              <div className="text-xl font-bold">RRP {issue.priceDisplay}</div>
            </div>
          </div>
        </div>

        <div className="pt-2 border-t justify-center sm:justify-between border-gray-700 flex gap-1">
          <Button
            className={"rounded-none text-sm !p-2 sm:!py-4 h-auto rounded-full h-[50px] whitespace-nowrap"}
            color={"white"}
            size="sm"
            disabled={store.orderedAmount <= 0}
            onClick={onAddToBasketButtonClick}
          >
            Add to Basket
          </Button>
          <div className="flex items-center">
            <Button
              variant="outlined"
              color="red"
              onClick={onDec}
              disabled={!canDec}
              className="h-12 !py-0 !px-3 md:!px-3 !border-light-grey/50 !text-white text-md hover:!bg-red-500 hover:!border-red-500 transition rounded-r-none"
              aria-label="decrease"
            >
              -
            </Button>

            <input
              type="number"
              name="quantity"
              min={0}
              max={
                issue.issueOnSale > new Date()
                  ? MAX_ISSUES_AVAILABLE
                  : issue.stock.issuesAvailableIncludingBasket
              }
              value={store.orderedAmount}
              onChange={(e) => {
                const parsed = parseInt(e.target.value || "0", 10) || 0;
                store.onCopiesChange(
                  Math.max(0, Math.min(parsed, MAX_ISSUES_AVAILABLE - issue.stock.inBasket))
                );
              }}
              className="w-8 md:w-10 h-12 text-center bg-gray-700 text-white border border-gray-600 focus:outline-none !appearance-none rounded-none"
            />

            <Button
              variant="outlined"
              color="green"
              onClick={onInc}
              disabled={!canInc}
              className="h-12 !py-0 !px-3 md:!px-3 !border-light-grey/50 !text-white text-md hover:!bg-green-500 hover:!border-green-500 transition rounded-l-none"
              aria-label="increase"
            >
              +
            </Button>
          </div>
        </div>
      </div>
    </div >
  );
});